/**
 * Helpers de color para los overlays del mapa de vehículos.
 *
 * Viven acá y no en el servicio de dibujo porque el panel también los necesita:
 * muestra el color con el que va a arrancar el degradado antes de dibujarlo.
 */

/** Interpola dos hex en RGB. `t` 0 = `a`, 1 = `b`. */
export function mezclarColores(a: string, b: string, t: number): string {
  const ca = canalesDeHex(a);
  const cb = canalesDeHex(b);
  if (!ca || !cb) return b;

  const f = Math.min(1, Math.max(0, t));
  return `#${ca
    .map((valor, i) =>
      Math.round(valor + (cb[i] - valor) * f)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/**
 * Complementario: el mismo color con el tono girado 180°, manteniendo
 * saturación y luminosidad. Girar el tono y no invertir los canales RGB
 * mantiene el par igual de vivo y de oscuro.
 */
export function colorComplementario(hex: string): string {
  const canales = canalesDeHex(hex);
  if (!canales) return hex;

  const [r, g, b] = canales.map((c) => c / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const luz = (max + min) / 2;
  const delta = max - min;

  // Gris: sin tono que girar, se devuelve tal cual.
  if (!delta) return hex;

  const saturacion = luz > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let tono: number;
  if (max === r) tono = ((g - b) / delta) % 6;
  else if (max === g) tono = (b - r) / delta + 2;
  else tono = (r - g) / delta + 4;
  tono = (tono * 60 + 360) % 360;

  return hslAHex((tono + 180) % 360, saturacion, luz);
}

/**
 * Color del espectro para un avance `t` (0..1): gira el tono partiendo del
 * color base, con luminosidad y croma constantes.
 *
 * El giro va en OKLCh y no en HSL porque HSL miente sobre la luminosidad: con
 * la misma "L", un amarillo verdoso se ve clarísimo y un azul oscuro. Girando
 * el tono en HSL, media rampa quedaba lavada sobre el mapa claro — fallaba la
 * banda de luminosidad y el contraste al validarla. En OKLCh todos los tonos
 * pesan visualmente igual.
 *
 * El giro da más de una vuelta a propósito. El color sólo tiene que distinguir
 * tramos que están cerca entre sí, así que no importa que dos zonas alejadas
 * repitan color: dando varias vueltas, cada zona con solapamientos recupera el
 * rango entero en vez de quedarse con una porción del espectro.
 */
export function colorDelEspectro(base: string, t: number): string {
  const tonoBase = hexAOklch(base)?.tono ?? 25;
  const avance = Math.min(1, Math.max(0, t));

  return oklchAHex(
    LUZ_ESPECTRO,
    CROMA_ESPECTRO,
    (tonoBase + avance * GIRO_ESPECTRO) % 360,
  );
}

/**
 * Cuánto gira el tono de punta a punta del espectro, en grados. Más de una
 * vuelta: más vueltas = más separación entre pasadas vecinas, a costa de
 * repetir colores entre zonas distintas del recorrido.
 */
const GIRO_ESPECTRO = 720;

/**
 * Versión chillona de un color: mismo tono, subido de croma y de luminosidad.
 *
 * Mezclarlo con blanco lo aclara pero lo apaga —le baja el croma— y sobre el
 * mapa termina viéndose lavado. Subiendo el croma en OKLCh queda claro Y vivo,
 * que es lo que hace que el movimiento se despegue de la traza.
 */
export function colorVivo(hex: string): string {
  const oklch = hexAOklch(hex);
  if (!oklch) return hex;

  // El croma pedido es más del que entra en sRGB a propósito: `oklchAHex` lo
  // baja hasta que el color entre, o sea que devuelve el más saturado posible
  // para ese tono. Y la luz no sube más que esto porque cuanto más claro, menos
  // croma admite el gamut: pasado este punto el color se apaga en vez de
  // gritar.
  return oklchAHex(Math.max(oklch.luz, 0.7), 0.35, oklch.tono);
}

/**
 * Luminosidad y croma fijos del espectro, en OKLCh. Salieron de validar la
 * rampa: dentro de la banda de luminosidad, por encima del piso de croma y con
 * contraste >= 3:1 contra el mapa claro en todos los tonos.
 */
const LUZ_ESPECTRO = 0.62;
const CROMA_ESPECTRO = 0.16;

function hexAOklch(
  hex: string,
): { luz: number; croma: number; tono: number } | null {
  const canales = canalesDeHex(hex);
  if (!canales) return null;

  const [r, g, b] = canales.map((c) => aLineal(c / 255));

  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

  const luz = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s;
  const a = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s;
  const bb = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;

  return {
    luz,
    croma: Math.hypot(a, bb),
    tono: (Math.atan2(bb, a) * (180 / Math.PI) + 360) % 360,
  };
}

/**
 * OKLCh a hex. Un tono puede pedir más croma del que entra en sRGB, así que si
 * el color se sale del gamut se baja el croma hasta que entre.
 */
function oklchAHex(luz: number, croma: number, tono: number): string {
  const radianes = (tono * Math.PI) / 180;

  for (let intento = 0; intento < 40; intento++) {
    const c = croma * (1 - intento * 0.05);
    const a = c * Math.cos(radianes);
    const b = c * Math.sin(radianes);

    const l_ = luz + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = luz - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = luz - 0.0894841775 * a - 1.291485548 * b;
    const l = l_ ** 3;
    const m = m_ ** 3;
    const s = s_ ** 3;

    const rgb = [
      4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
      -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
      -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ].map(aGamma);

    if (rgb.every((v) => v >= -0.001 && v <= 1.001)) {
      return `#${rgb
        .map((v) =>
          Math.round(Math.min(1, Math.max(0, v)) * 255)
            .toString(16)
            .padStart(2, '0'),
        )
        .join('')}`;
    }
  }

  return '#000000';
}

function aLineal(canal: number): number {
  return canal <= 0.04045
    ? canal / 12.92
    : Math.pow((canal + 0.055) / 1.055, 2.4);
}

function aGamma(canal: number): number {
  return canal <= 0.0031308
    ? 12.92 * canal
    : 1.055 * Math.pow(canal, 1 / 2.4) - 0.055;
}

function hexAHsl(
  hex: string,
): { tono: number; saturacion: number; luz: number } | null {
  const canales = canalesDeHex(hex);
  if (!canales) return null;

  const [r, g, b] = canales.map((c) => c / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const luz = (max + min) / 2;
  const delta = max - min;

  if (!delta) return { tono: 0, saturacion: 0, luz };

  const saturacion = luz > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  let tono: number;
  if (max === r) tono = ((g - b) / delta) % 6;
  else if (max === g) tono = (b - r) / delta + 2;
  else tono = (r - g) / delta + 4;

  return { tono: (tono * 60 + 360) % 360, saturacion, luz };
}

function canalesDeHex(hex: string): number[] | null {
  const limpio = (hex || '').replace('#', '');
  if (limpio.length !== 6) return null;
  const valores = [0, 2, 4].map((i) => parseInt(limpio.slice(i, i + 2), 16));
  return valores.some((v) => Number.isNaN(v)) ? null : valores;
}

function hslAHex(tono: number, saturacion: number, luz: number): string {
  const c = (1 - Math.abs(2 * luz - 1)) * saturacion;
  const x = c * (1 - Math.abs(((tono / 60) % 2) - 1));
  const m = luz - c / 2;

  const tabla: [number, number, number][] = [
    [c, x, 0],
    [x, c, 0],
    [0, c, x],
    [0, x, c],
    [x, 0, c],
    [c, 0, x],
  ];
  const [r, g, b] = tabla[Math.floor(tono / 60) % 6];

  return `#${[r, g, b]
    .map((canal) =>
      Math.round((canal + m) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}
